"use client";

import { useState } from "react";
import Link from "next/link";
import { CheckCircle2, GitCompareArrows, Loader2, Star } from "lucide-react";

import { submitServiceReview } from "@/lib/cv-builder/polish-client";
import { withCvBuilderAnalyticsContext } from "@/components/cv-builder/cv-builder-analytics";
import { Button } from "@/components/ui/button";
import { Textarea } from "@/components/ui/textarea";
import { trackEvent } from "@/lib/analytics";

interface PolishSuccessStepProps {
  reviewTarget: { jobId: string; paymentId: string } | null;
  analyticsContext?: UmamiEventData;
  /** Deep link to the before/after comparison for the polish just purchased. */
  compareHref: string;
  compareLabel: string;
  onClose: () => void;
}

/**
 * Post-purchase confirmation and the service review prompt. Split out of
 * polish-dialog so that file stays within the repo's file size guidance.
 */
export function PolishSuccessStep({
  reviewTarget,
  analyticsContext,
  compareHref,
  compareLabel,
  onClose,
}: PolishSuccessStepProps) {
  const [rating, setRating] = useState(0);
  const [comment, setComment] = useState("");
  const [isSubmitting, setIsSubmitting] = useState(false);
  const [submitted, setSubmitted] = useState(false);
  const [error, setError] = useState<string | null>(null);

  const handleSubmit = async () => {
    if (!reviewTarget || rating < 1) {
      setError("Pilih rating dulu ya.");
      return;
    }

    setIsSubmitting(true);
    setError(null);

    try {
      await submitServiceReview({
        serviceType: "cv_builder_polish",
        paymentId: reviewTarget.paymentId,
        relatedEntityType: "cv_builder_polish_job",
        relatedEntityId: reviewTarget.jobId,
        rating,
        comment,
        source: "cv_builder_polish_success_dialog",
      });
      setSubmitted(true);
      trackEvent(
        "service_review_submitted",
        withCvBuilderAnalyticsContext(analyticsContext, {
          source: "cv_builder_polish_success_dialog",
          service_type: "cv_builder_polish",
          rating,
        }),
      );
    } catch (err) {
      console.error("Failed to submit CV builder polish review", err);
      setError("Gagal menyimpan review. Coba lagi sebentar lagi.");
    } finally {
      setIsSubmitting(false);
    }
  };

  return (
    <div
      className="flex flex-col items-center justify-center gap-4 py-6 text-center"
      role="status"
      aria-live="polite"
    >
      <CheckCircle2 className="h-10 w-10 text-primary" aria-hidden="true" />
      <div>
        <p className="font-semibold text-foreground">
          CV kamu sudah lebih siap dibaca recruiter
        </p>
        <p className="mt-1 text-sm text-muted-foreground">
          Preview sudah diperbarui. Cek lagi sebelum download.
        </p>
      </div>

      {/* The moment the buyer most wants to verify what they paid for, so the
          comparison is offered here rather than only from the builder. */}
      <Button asChild variant="outline">
        <Link href={compareHref}>
          <GitCompareArrows className="mr-2 h-4 w-4" aria-hidden="true" />
          {compareLabel}
        </Link>
      </Button>

      <div className="w-full rounded-2xl border border-border bg-card p-4 text-left">
        <p className="text-sm font-semibold text-foreground">
          Bantu kami nilai hasil AI Polish ini
        </p>
        <p className="mt-1 text-xs leading-5 text-muted-foreground">
          Review kamu membantu kami tahu apakah hasilnya sudah cukup jelas,
          jujur, dan berguna untuk pencari kerja lain.
        </p>
        <div className="mt-3 flex gap-1">
          {[1, 2, 3, 4, 5].map((value) => (
            <button
              key={value}
              type="button"
              onClick={() => setRating(value)}
              className="rounded-md p-1 text-amber-400 transition hover:scale-105 focus-visible:outline-none focus-visible:ring-2 focus-visible:ring-ring"
              aria-label={`Beri rating ${value} dari 5`}
              disabled={submitted}
            >
              <Star
                className="h-6 w-6"
                fill={value <= rating ? "currentColor" : "none"}
              />
            </button>
          ))}
        </div>
        <Textarea
          value={comment}
          onChange={(event) => setComment(event.target.value)}
          disabled={submitted}
          placeholder="Opsional: apa yang paling membantu atau masih kurang?"
          className="mt-3 min-h-20 text-sm"
          maxLength={1000}
        />
        {error ? (
          <p className="mt-2 text-xs text-destructive">{error}</p>
        ) : null}
        {submitted ? (
          <p className="mt-2 text-xs font-medium text-primary">
            Review tersimpan. Terima kasih.
          </p>
        ) : null}
        <div className="mt-3 flex flex-col-reverse gap-2 sm:flex-row sm:justify-end">
          <Button type="button" variant="outline" onClick={onClose}>
            Lihat hasil
          </Button>
          <Button
            type="button"
            onClick={handleSubmit}
            disabled={isSubmitting || submitted}
          >
            {isSubmitting ? (
              <Loader2 className="mr-2 h-4 w-4 animate-spin" aria-hidden="true" />
            ) : null}
            Simpan review
          </Button>
        </div>
      </div>
    </div>
  );
}
